import * as React from "react";
import { Dialog } from "radix-ui";
import { Button, Eyebrow, cn, dialogBodyClass, dialogContentClass, dialogOverlayClass } from "@ozpanel/ui";

// FormDialog — the create/edit surface the console had none of: every "Add" was simply
// missing, so a platform admin could inspect and destroy things but never make one.
//
// Field specs are the SAME shape DataForm renders (module.SettingsField), so a create form and
// a settings form are described the same way and there is one vocabulary, not two.

export interface FormFieldSpec {
  key: string;
  label: string;
  type?: "string" | "int" | "bool" | "enum" | "password";
  help?: string;
  placeholder?: string;
  required?: boolean;
  options?: { value: string; label: string }[];
  defaultValue?: unknown;
}

export type FormValues = Record<string, unknown>;

const inputCls =
  "w-full rounded-lg border border-line2 bg-bg px-3 py-2 text-[13.5px] text-fg outline-none transition-colors focus:border-accent";

export function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  fields,
  submitLabel = "Create",
  pending,
  error,
  onSubmit,
}: {
  open: boolean;
  onOpenChange(o: boolean): void;
  title: string;
  description?: string;
  fields: FormFieldSpec[];
  submitLabel?: string;
  pending?: boolean;
  error?: string | null;
  onSubmit(values: FormValues): void;
}) {
  const initial = React.useMemo(() => {
    const v: FormValues = {};
    for (const f of fields) v[f.key] = f.defaultValue ?? (f.type === "bool" ? false : "");
    return v;
  }, [fields]);
  const [values, setValues] = React.useState<FormValues>(initial);
  React.useEffect(() => { if (open) setValues(initial); }, [open, initial]);

  const missing = fields.filter((f) => f.required && !String(values[f.key] ?? "").trim()).map((f) => f.label);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={dialogOverlayClass} />
        <Dialog.Content className={dialogContentClass}>
          <form
            className="flex min-h-0 flex-1 flex-col"
            onSubmit={(e) => { e.preventDefault(); if (missing.length === 0) onSubmit(values); }}
          >
            <div className="border-b border-line px-5 py-4">
              <Dialog.Title className="text-[16px] font-[680] tracking-[-0.015em]">{title}</Dialog.Title>
              {description && (
                <Dialog.Description className="mt-1.5 text-[13px] leading-relaxed text-muted">
                  {description}
                </Dialog.Description>
              )}
            </div>

            <div className={cn(dialogBodyClass, "space-y-4 px-5 py-4")}>
              {error && (
                <p className="rounded-lg border border-[color-mix(in_oklab,var(--ozp-crit)_40%,transparent)] bg-critsoft px-3 py-2.5 text-[13px] text-crit">
                  {error}
                </p>
              )}
              {fields.map((f) => (
                <div key={f.key}>
                  <label htmlFor={f.key} className="mb-1.5 flex items-center gap-2 text-[12.5px] font-medium text-muted">
                    {f.label}
                    {f.required && <Eyebrow className="text-[9px]">required</Eyebrow>}
                  </label>
                  {f.type === "bool" ? (
                    <button
                      id={f.key}
                      type="button"
                      role="switch"
                      aria-checked={values[f.key] === true}
                      onClick={() => setValues((p) => ({ ...p, [f.key]: !p[f.key] }))}
                      className={cn(
                        "relative h-6 w-[42px] rounded-full border transition-colors",
                        values[f.key] ? "border-[var(--ozp-accent-line)] bg-accentsoft" : "border-line2 bg-surface3",
                      )}
                    >
                      <span className={cn("absolute top-[3px] size-4 rounded-full transition-transform",
                        values[f.key] ? "translate-x-[21px] bg-accent" : "translate-x-[3px] bg-muted")} />
                    </button>
                  ) : f.options ? (
                    <select id={f.key} className={inputCls} value={String(values[f.key] ?? "")}
                      onChange={(e) => setValues((p) => ({ ...p, [f.key]: e.target.value }))}>
                      <option value="">— choose —</option>
                      {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : (
                    <input
                      id={f.key}
                      className={inputCls}
                      type={f.type === "int" ? "number" : f.type === "password" ? "password" : "text"}
                      placeholder={f.placeholder}
                      value={String(values[f.key] ?? "")}
                      onChange={(e) =>
                        setValues((p) => ({ ...p, [f.key]: f.type === "int" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value }))
                      }
                    />
                  )}
                  {f.help && <p className="mt-1.5 text-[12px] leading-relaxed text-muted">{f.help}</p>}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3.5">
              <Dialog.Close asChild>
                <Button type="button" variant="ghost" disabled={pending}>Cancel</Button>
              </Dialog.Close>
              <Button type="submit" variant="accent" disabled={pending || missing.length > 0}
                title={missing.length ? `Still needed: ${missing.join(", ")}` : undefined}>
                {pending ? "Working…" : submitLabel}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
